import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * "Complete Your Look" card on the product page.
 *
 * Markup, product choice and prices are rendered by blocks/product-look-bundle.liquid.
 * This only totals the ticked items, and adds them to the bag in one request.
 *
 * The first tile is the product being viewed: it follows the shade picked above, its tick is locked on,
 * and when that variant is already in the bag it is marked "In your bag" and left out of the add (it still
 * counts towards the minimum number of items the Shopify discount needs).
 */
class ProductLookBundle extends HTMLElement {
  /** Variant ids currently in the bag. @type {Set<number>} */
  #cartVariantIds = new Set();

  connectedCallback() {
    this.addEventListener('change', this.#recalculate);
    this.querySelector('[data-bundle-add]')?.addEventListener('click', this.#add);
    document.addEventListener(ThemeEvents.variantUpdate, this.#syncAnchorVariant);
    window.addEventListener('pageshow', this.#resetTicks);
    document.addEventListener(ThemeEvents.cartUpdate, this.#refreshCart);
    this.#resetTicks();
    this.#refreshCart();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#recalculate);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#syncAnchorVariant);
    window.removeEventListener('pageshow', this.#resetTicks);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#refreshCart);
  }

  /** Reads the bag so the main product's "In your bag" state is always current. */
  #refreshCart = async () => {
    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
      this.#cartVariantIds = new Set((cart.items ?? []).map((item) => Number(item.variant_id)));
    } catch {
      // Leave the last known state; the add itself re-reads the bag.
    }
    this.#recalculate();
  };

  /**
   * Every look item starts ticked. Browsers restore an earlier visit's form state on refresh and on
   * back/forward, so the ticks are put back explicitly instead of trusting the markup's `checked`.
   */
  #resetTicks = () => {
    for (const box of this.querySelectorAll('[data-bundle-check]')) {
      if (box instanceof HTMLInputElement) box.checked = true;
    }
    this.#recalculate();
  };

  /** @param {Event} event */
  #syncAnchorVariant = (event) => {
    const id = /** @type {CustomEvent} */ (event).detail?.resource?.id;
    const select = this.querySelector('[data-bundle-item] select[data-bundle-variant]');
    if (!id || !(select instanceof HTMLSelectElement)) return;
    if (![...select.options].some((option) => option.value === String(id))) return;
    select.value = String(id);
    this.#recalculate();
  };

  get #strings() {
    return this.querySelector('[data-bundle-strings]')?.content.firstElementChild?.dataset ?? {};
  }

  /** @returns {{ variantId: number, price: number, inCart: boolean }[]} */
  #selection() {
    return [...this.querySelectorAll('[data-bundle-item]')]
      // The main product has no tick box: it is always part of the bundle.
      .filter((item) => item.hasAttribute('data-bundle-main') || item.querySelector('[data-bundle-check]')?.checked)
      .map((item) => {
        const control = item.querySelector('[data-bundle-variant]');
        const option = control instanceof HTMLSelectElement ? control.selectedOptions[0] : control;
        const variantId = Number(control?.value);
        return {
          variantId,
          price: Number(option?.dataset.variantPrice),
          inCart: item.hasAttribute('data-bundle-main') && this.#cartVariantIds.has(variantId),
        };
      })
      .filter((entry) => entry.variantId && !Number.isNaN(entry.price));
  }

  /**
   * Bare base-currency number. The shop's currency switcher converts `.money` nodes to the
   * shopper's display currency and draws the symbol, so no currency is formatted here.
   * @param {number} cents
   */
  #amount(cents) {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100);
  }

  #recalculate = () => {
    // A variant change also changes that tile's displayed price.
    for (const item of this.querySelectorAll('[data-bundle-item]')) {
      const control = item.querySelector('[data-bundle-variant]');
      const option = control instanceof HTMLSelectElement ? control.selectedOptions[0] : control;
      const priceEl = item.querySelector('[data-bundle-price]');
      if (priceEl && option?.dataset.priceText) priceEl.textContent = option.dataset.priceText;
    }

    const selection = this.#selection();
    // What the shopper is about to add: the main product is left out when it is already in the bag.
    const toAdd = selection.filter((entry) => !entry.inCart);
    const subtotal = toAdd.reduce((sum, entry) => sum + entry.price, 0);
    const mainInBag = selection.some((entry) => entry.inCart);
    const inBagLabel = this.querySelector('[data-bundle-inbag]');
    if (inBagLabel instanceof HTMLElement) inBagLabel.hidden = !mainInBag;
    const percent = Number(this.dataset.discountPercent) || 0;
    // The Shopify discount needs a minimum number of items, so the saving only shows once enough are ticked.
    const minItems = Number(this.dataset.minItems) || 1;
    const discount = percent > 0 && selection.length >= minItems ? Math.round((subtotal * percent) / 100) : 0;
    const strings = this.#strings;

    const label = this.querySelector('[data-bundle-label]');
    if (label) {
      label.textContent = toAdd.length
        ? ((toAdd.length === 1 ? strings.one : strings.many)?.replace('{count}', String(toAdd.length)) ?? '')
        : '';
    }

    const was = this.querySelector('[data-bundle-was]');
    const saved = this.querySelector('[data-bundle-saved]');
    if (was) {
      was.hidden = discount === 0;
      const wasAmount = was.querySelector('.money');
      if (wasAmount) wasAmount.textContent = discount ? this.#amount(subtotal) : '';
    }
    if (saved) {
      saved.hidden = discount === 0;
      const savedText = saved.querySelector('[data-bundle-saved-text]');
      const savedAmount = saved.querySelector('[data-bundle-saved-amount]');
      if (savedText) savedText.textContent = strings.saved ?? '';
      if (savedAmount) savedAmount.textContent = discount ? this.#amount(discount) : '';
    }

    const total = this.querySelector('[data-bundle-total]');
    if (total) total.textContent = this.#amount(subtotal - discount);

    const button = this.querySelector('[data-bundle-add]');
    if (button instanceof HTMLButtonElement) button.disabled = toAdd.length === 0;
  };

  #add = async () => {
    const button = this.querySelector('[data-bundle-add]');
    const errorEl = this.querySelector('[data-bundle-error]');
    if (!(button instanceof HTMLButtonElement)) return;

    // Re-read the bag first: the main product may have been added since the page loaded.
    await this.#refreshCart();
    const selection = this.#selection().filter((entry) => !entry.inCart);
    if (!selection.length) return;

    if (errorEl) errorEl.hidden = true;
    button.disabled = true;
    const strings = this.#strings;
    if (strings.adding) button.textContent = strings.adding;

    const sectionIds = [];
    for (const component of document.querySelectorAll('cart-items-component')) {
      const id = component instanceof HTMLElement ? component.dataset.sectionId : undefined;
      if (id && !sectionIds.includes(id)) sectionIds.push(id);
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: selection.map((entry) => ({ id: entry.variantId, quantity: 1 })),
          sections: sectionIds.join(','),
        }),
      });
      const result = await response.json();

      if (result.status) {
        throw new Error(result.description || result.message || 'Could not add to bag');
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
      this.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: 'product-look-bundle',
          itemCount: selection.length,
          sections: result.sections,
        })
      );
    } catch (error) {
      if (errorEl) {
        errorEl.textContent = error instanceof Error ? error.message : String(error);
        errorEl.hidden = false;
      }
    } finally {
      // On the product page nothing re-renders this card after an add (the drawer does, the page doesn't),
      // so the button has to be put back here, success or not.
      if (strings.add) button.textContent = strings.add;
      this.#recalculate();
    }
  };
}

if (!customElements.get('product-look-bundle')) {
  customElements.define('product-look-bundle', ProductLookBundle);
}
